import type { MemoryStore } from './memory-store';
import { createMemoryStore } from './memory-store';

// Bump when cached HTML shape changes in a way that would break old clients
// (e.g. a slider library migration). On mismatch the whole namespace is cleared
// on first access, so stale content is never served. Monotonically increase —
// do not reuse old numbers.
const CACHE_VERSION = '2';
const VERSION_KEY = '__schema_version';
const NAMESPACE = 'memory';

const storePromises = new WeakMap<Storage, Promise<MemoryStore>>();

async function initStore(storage: Storage): Promise<MemoryStore> {
  const store = await createMemoryStore(NAMESPACE, storage);
  try {
    const current = await store.getItem(VERSION_KEY);
    if (current !== CACHE_VERSION) {
      await store.clearNamespace();
      await store.setItem(VERSION_KEY, CACHE_VERSION);
    }
  } catch {
    // If the version check fails, fall through with the store as-is.
  }
  return store;
}

function getStore(storage: Storage): Promise<MemoryStore> {
  let promise = storePromises.get(storage);
  if (!promise) {
    promise = initStore(storage);
    storePromises.set(storage, promise);
  }
  return promise;
}

class StringMemory {
  private readonly storePromise: Promise<MemoryStore>;

  constructor(storage: Storage) {
    this.storePromise = getStore(storage);
  }

  async get<T extends () => string | Promise<string>>(key: string, callback: T) {
    let item: string | null = null;
    try {
      const store = await this.storePromise;
      item = await store.getItem(key);
    } catch {
      // Ignore store read errors; we will fetch fresh below
    }

    if (item) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log(
          `Fetched key "${key}" correctly, but did not return the stored response. Use production environment to enable.`,
        );
        return callback();
      }
      return item;
    }

    const result = await callback();

    try {
      const store = await this.storePromise;
      await store.setItem(key, result);
    } catch {
      // Swallow any caching errors (quota, blocked storage, etc.)
    }

    return result;
  }

  delete(key: string) {
    void this.storePromise.then((store) => store.removeItem(key)).catch(() => {
      // ignore delete errors
    });
  }
}

export default (storage: Storage) => new StringMemory(storage);
